import { Anchor, Button, PasswordInput, Stack, TextInput } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, type Schemas } from "../../api/client";
import { errorMessage } from "../../shared/session/errors";
import { afterSignIn } from "../../shared/session/invitation";
import { adoptSession } from "../../shared/session/session";
import { FormError, Page } from "../../shared/ui/Page";

export function SignInPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [mfa, setMfa] = useState<Schemas["MFAChallenge"] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const done = (s: Schemas["Session"]) => {
    adoptSession(s.access_token, s.expires_at);
    navigate(afterSignIn(params.get("next")));
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { data, error } = await api.POST("/auth/login", { body: { code, password } });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    if (data.session) return done(data.session);
    if (data.mfa) setMfa(data.mfa);
  };
  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfa) return;
    setBusy(true);
    setError(null);
    const { data, error } = await api.POST("/auth/login/verify", {
      body: { challenge_id: mfa.challenge_id, code: otp, channel: mfa.channels[0] ?? "sms" },
    });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    done(data);
  };

  if (mfa) {
    return (
      <Page title={t("signin.mfa_title")}>
        <form onSubmit={confirm}>
          <TextInput
            label={
              mfa.channels.includes("totp") ? t("signin.mfa_totp") : t("signin.mfa_sms", { phone: mfa.masked_phone })
            }
            inputMode="numeric"
            autoComplete="one-time-code"
            value={otp}
            onChange={(e) => setOtp(e.currentTarget.value.trim())}
            required
            mb="md"
          />
          <FormError message={error} />
          <Button type="submit" fullWidth loading={busy} mt="sm">
            {t("signin.mfa_submit")}
          </Button>
        </form>
      </Page>
    );
  }
  return (
    <Page title={t("signin.title")}>
      <form onSubmit={submit}>
        <TextInput
          label={t("signin.code")}
          description={t("signin.code_hint")}
          autoComplete="username"
          autoCapitalize="characters"
          value={code}
          onChange={(e) => setCode(e.currentTarget.value)}
          required
          mb="sm"
        />
        <PasswordInput
          label={t("signin.password")}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
          required
          mb="md"
        />
        <FormError message={error} />
        <Button type="submit" fullWidth loading={busy} mt="sm">
          {t("signin.submit")}
        </Button>
      </form>
      <Stack gap={4}>
        <Anchor component={Link} to="/forgot-password">
          {t("signin.forgot_password")}
        </Anchor>
        <Anchor component={Link} to="/recover-code">
          {t("signin.forgot_code")}
        </Anchor>
        <Anchor component={Link} to="/signup">
          {t("signin.no_account")}
        </Anchor>
      </Stack>
    </Page>
  );
}
